import { 
  format, 
  parseISO, 
  startOfDay, 
  endOfDay, 
  isValid,
  isSameDay as dateFnsIsSameDay,
  formatDistanceToNow
} from 'date-fns';
import { 
  utcToZonedTime, 
  zonedTimeToUtc, 
  formatInTimeZone 
} from 'date-fns-tz';
import { logError } from '../services/logService';
import { ptBR } from 'date-fns/locale';

// Configuração do fuso horário
const TIMEZONE = 'America/Sao_Paulo';

// Formatos padrão
export const DATE_FORMATS = {
  firebase: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  display: 'dd/MM/yyyy',
  displayWithTime: 'dd/MM/yyyy HH:mm',
  displayFull: 'dd/MM/yyyy HH:mm:ss',
  displayRelative: 'relative'
};

// Converte uma data para o fuso horário local
export function toLocalTime(date) {
  try {
    if (!date) return null;
    
    // Se for string, converte para Date
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    if (!isValid(dateObj)) {
      throw new Error('Data inválida');
    }
    
    return utcToZonedTime(dateObj, TIMEZONE);
  } catch (error) {
    logError('Erro ao converter para hora local', { date, error });
    return null;
  }
}

// Converte uma data local para UTC
export function toUTC(date) {
  try {
    if (!date) return null;
    
    // Se for string, converte para Date
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    if (!isValid(dateObj)) {
      throw new Error('Data inválida');
    }
    
    return zonedTimeToUtc(dateObj, TIMEZONE);
  } catch (error) {
    logError('Erro ao converter para UTC', { date, error });
    return null;
  }
}

// Formata uma data para exibição
export function formatDateTime(date, formatStr = DATE_FORMATS.displayWithTime) {
  try {
    if (!date) return '';
    
    let dateObj;
    
    // Se for string, tenta converter para Date
    if (typeof date === 'string') {
      // Tenta primeiro como ISO
      dateObj = parseISO(date);
      
      // Se não for válido, tenta como timestamp
      if (!isValid(dateObj)) {
        dateObj = new Date(date);
      }
    } else {
      dateObj = date;
    }
    
    // Verifica se a data é válida
    if (!isValid(dateObj)) {
      console.warn('Data inválida:', date);
      return '';
    }
    
    // Se for formato relativo, usa formatDistanceToNow
    if (formatStr === DATE_FORMATS.displayRelative) {
      return formatDistanceToNow(dateObj, { 
        addSuffix: true,
        locale: ptBR 
      });
    }
    
    // Formata a data
    const formattedDate = formatInTimeZone(dateObj, TIMEZONE, formatStr);
    console.log('Formatação de data:', {
      input: date,
      dateObj,
      formatted: formattedDate
    });
    
    return formattedDate;
  } catch (error) {
    console.error('Erro ao formatar data/hora:', error, {
      date,
      formatStr
    });
    return '';
  }
}

// Formata uma data para o Firestore
export function formatDateForFirebase(date) {
  try {
    if (!date) return null;
    
    return formatInTimeZone(date, TIMEZONE, DATE_FORMATS.firebase);
  } catch (error) {
    logError('Erro ao formatar data para Firebase', { date, error });
    return null;
  }
}

// Obtém a data/hora atual em UTC
export function getCurrentDateTime() {
  const now = new Date();
  return now.toISOString();
}

// Obtém o início do dia em UTC
export function getStartOfDay(date) {
  try {
    const localDate = toLocalTime(date);
    if (!localDate) return null;
    
    const startDate = startOfDay(localDate);
    return toUTC(startDate);
  } catch (error) {
    logError('Erro ao obter início do dia', { date, error });
    return null;
  }
}

// Obtém o fim do dia em UTC
export function getEndOfDay(date) {
  try {
    const localDate = toLocalTime(date);
    if (!localDate) return null;
    
    const endDate = endOfDay(localDate);
    return toUTC(endDate);
  } catch (error) {
    logError('Erro ao obter fim do dia', { date, error });
    return null;
  }
}

// Verifica se duas datas são do mesmo dia
export function isSameDay(dateA, dateB) {
  try {
    if (!dateA || !dateB) return false;
    
    const dateObjA = typeof dateA === 'string' ? parseISO(dateA) : dateA;
    const dateObjB = typeof dateB === 'string' ? parseISO(dateB) : dateB;
    
    if (!isValid(dateObjA) || !isValid(dateObjB)) {
      throw new Error('Data inválida');
    }
    
    return dateFnsIsSameDay(dateObjA, dateObjB);
  } catch (error) {
    logError('Erro ao comparar datas', { dateA, dateB, error });
    return false;
  }
}

// Converte dia da semana do JavaScript (0-6) para o sistema (1-7)
export function convertJSDayToSystemDay(jsDay) {
  const mapping = {
    0: 7,  // Domingo -> 7
    1: 1,  // Segunda -> 1
    2: 2,  // Terça -> 2
    3: 3,  // Quarta -> 3
    4: 4,  // Quinta -> 4
    5: 5,  // Sexta -> 5
    6: 6   // Sábado -> 6
  };
  
  return mapping[jsDay] || null;
}

// Array com os nomes dos meses em português
export const months = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
];

// Parse uma data do formato do Firebase
export function parseDateFromFirebase(dateStr) {
  try {
    if (!dateStr) return null;
    return parseISO(dateStr);
  } catch (error) {
    logError('Erro ao fazer parse da data do Firebase', { dateStr, error });
    return null;
  }
}